import type { NodeViewProps } from '@tiptap/react';

import { cn } from '@repo/lib/cn';
import { NodeViewWrapper } from '@tiptap/react';

import { VideoPlayer, VideoPlayerControls, VideoPlayerOverlay } from '@/components/ui/media-player';
import { RemoveFloatButton } from '@/components/ui/tiptap/tiptap-ui-primitives/remove-button';
import { ResizableButton, useResizableMedia } from '@/components/ui/tiptap/tiptap-ui-primitives/resizable-button';

export function VideoNodeComponent(videoPlayerNodeComponentProps: NodeViewProps) {
  const { node, editor, getPos, selected } = videoPlayerNodeComponentProps;
  const { title, src, poster, maxWidth, maxHeight, textAlign } = node.attrs;
  const isEditable: boolean = editor.isEditable;

  const { ref, containerRef, handleMediaLoad, handleMouseDown } = useResizableMedia<HTMLVideoElement>(videoPlayerNodeComponentProps, (el) => ({
    width: el.videoWidth || el.clientWidth,
    height: el.videoHeight || el.clientHeight,
  }));

  const getVideoStyle = () => {
    const style: React.CSSProperties = {};
    if (maxWidth) {
      style.maxWidth = `${maxWidth}px`;
    }
    if (maxHeight) {
      style.maxHeight = `${maxHeight}px`;
    }
    return style;
  };

  // Get container style based on alignment
  const getContainerStyle = (): React.CSSProperties => {
    switch (textAlign) {
      case 'center':
      case 'justify':
        return { display: 'flex', justifyContent: 'center' };
      case 'right':
        return { display: 'flex', justifyContent: 'flex-end' };
      case 'left':
        return { display: 'flex', justifyContent: 'flex-start' };
      default:
        return { display: 'flex', justifyContent: 'flex-start' };
    }
  };

  return (
    <NodeViewWrapper ref={containerRef} className={cn('tiptap video-node relative overflow-hidden')} style={getContainerStyle()}>
      <VideoPlayer ref={ref} src={src} title={title} poster={poster} containerStyle={getVideoStyle()} onLoadedMetadata={handleMediaLoad} className={'relative inline-block max-w-full rounded-md'}>
        <VideoPlayerControls />
        <VideoPlayerOverlay />
        {selected && isEditable && (
          <>
            <ResizableButton side="left" handleMouseDown={handleMouseDown} />
            <ResizableButton side="right" handleMouseDown={handleMouseDown} />
            <RemoveFloatButton editor={editor} pos={getPos()} />
          </>
        )}
      </VideoPlayer>
    </NodeViewWrapper>
  );
}
